import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { message } from "antd";
import { getProject, updateProjectImage } from "../services/projectServices";
import {
    getReport,
    getReportSections,
    createSection,
    updateSection,
    deleteSection as deleteSectionAPI,
    addPhotoToSection,
    removePhotoFromSection,
    reorderPhotos,
    updateReportSettings,
    getLayoutOptions,
    generatePDF,
    downloadPDF,
    deleteReport as deleteReportAPI
} from "../services/reportsService";

const usePhotoReport = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const userDetails = useSelector(state => state.User?.userDetails);

    // Core state
    const [report, setReport] = useState(null);
    const [sections, setSections] = useState([]);
    const [projectData, setProjectData] = useState(null);
    const [projectImages, setProjectImages] = useState([]);
    const [layoutOptions, setLayoutOptions] = useState([]);
    const [loading, setLoading] = useState(true);

    // Settings state
    const [editTitle, setEditTitle] = useState("");
    const [editLayout, setEditLayout] = useState(2);
    const [claimNumber, setClaimNumber] = useState("");
    const [showConclusion, setShowConclusion] = useState(true);
    const [conclusion, setConclusion] = useState("");
    const [coverSettings, setCoverSettings] = useState({
        showCoverPhoto: true,
        showCompanyLogo: true,
        showCompanyName: true,
        showCreatedDate: true,
        showPhotoCount: true,
        showClaimNumber: true,
        coverPhotoId: null
    });

    // UI state
    const [activeSectionIndex, setActiveSectionIndex] = useState(0);
    const [generatingPDF, setGeneratingPDF] = useState(false);
    const [savingSettings, setSavingSettings] = useState(false);
    const [addingSection, setAddingSection] = useState(false);
    const [savingConclusion, setSavingConclusion] = useState(false);
    const [savingSectionTitle, setSavingSectionTitle] = useState(false);
    const [savingSectionSummary, setSavingSectionSummary] = useState(false);
    const [savingPhotoDescId, setSavingPhotoDescId] = useState(null);

    // Load sections helper
    const loadSections = useCallback((reportId) => {
        return dispatch(getReportSections(reportId))
            .then((sectionsData) => {
                const apiSections = Array.isArray(sectionsData) ? sectionsData : (sectionsData.data || []);
                setSections(apiSections);
                return apiSections;
            })
            .catch((error) => {
                console.error('Failed to load sections:', error);
                throw error;
            });
    }, [dispatch]);

    // Initialize data fetching
    useEffect(() => {
        const reportData = location.state?.report;
        const urlParams = new URLSearchParams(window.location.search);
        const reportIdFromUrl = urlParams.get('report_id');
        const reportIdFromPath = window.location.pathname.split('/').pop();
        const reportId = reportData?.id || reportIdFromUrl || reportIdFromPath;

        if (!reportId || reportId === 'project-photo-report') {
            message.error('Report ID not found');
            navigate('/projects');
            return;
        }

        setLoading(true);

        // Fetch fresh report data from API
        dispatch(getReport(reportId))
            .then((freshReport) => {
                setReport(freshReport);
                setEditTitle(freshReport.title);
                setEditLayout(freshReport.photos_per_page || freshReport.photosPerPage || 2);
                setClaimNumber(freshReport.claim_number || "");
                setShowConclusion(freshReport.show_conclusion ?? true);
                setConclusion(freshReport.conclusion || '');

                // Save company data if not already saved
                if (!freshReport.company_data && userDetails?.profiles?.[0]) {
                    const profile = userDetails.profiles[0];
                    const logoUrl = profile.logo_url || '';

                    const companyData = {
                        company_name: profile.organization_name || '',
                        company_logo: logoUrl,
                        inspector_name: profile.insp_fullname || ''
                    };
                    dispatch(updateReportSettings(freshReport.id, { company_data: companyData })).catch(() => { });
                }

                // Load cover settings from report
                if (freshReport.cover_settings) {
                    setCoverSettings({
                        showCoverPhoto: freshReport.cover_settings.show_cover_photo ?? true,
                        showCompanyLogo: freshReport.cover_settings.show_company_logo ?? true,
                        showCompanyName: freshReport.cover_settings.show_company_name ?? true,
                        showCreatedDate: freshReport.cover_settings.show_created_date ?? true,
                        showPhotoCount: freshReport.cover_settings.show_photo_count ?? true,
                        showClaimNumber: freshReport.cover_settings.show_claim_number ?? true,
                        coverPhotoId: freshReport.cover_settings.cover_photo_id || null
                    });
                }

                // Load layout options
                dispatch(getLayoutOptions(freshReport.id))
                    .then((response) => {
                        setLayoutOptions(response.data || response || []);
                    })
                    .catch((error) => {
                        console.error('Failed to load layout options:', error);
                        setLayoutOptions([2, 3, 4]);
                    });

                const projectId = freshReport.project_id || freshReport.projectId;

                if (!projectId) {
                    message.error('Project ID not found in report data');
                    navigate('/projects');
                    return;
                }

                // Load project data
                dispatch(getProject(projectId))
                    .then((response) => {
                        setProjectData(response);
                        const allImages = response?.inspection_images || [];
                        setProjectImages(allImages);

                        // Load sections from API
                        dispatch(getReportSections(freshReport.id))
                            .then((sectionsData) => {
                                const apiSections = Array.isArray(sectionsData) ? sectionsData : (sectionsData.data || []);

                                // If sections exist, use them
                                if (apiSections.length > 0) {
                                    setSections(apiSections);
                                    // Set default cover photo if not already set and save settings
                                    if (apiSections[0].photos?.length > 0 && !freshReport.cover_settings?.cover_photo_id) {
                                        const defaultCoverPhotoId = apiSections[0].photos[0].id;
                                        setCoverSettings(prev => ({
                                            ...prev,
                                            coverPhotoId: defaultCoverPhotoId
                                        }));
                                        // Save default settings
                                        dispatch(updateReportSettings(freshReport.id, {
                                            cover_settings: {
                                                show_cover_photo: true,
                                                show_company_logo: true,
                                                show_company_name: true,
                                                show_created_date: true,
                                                show_photo_count: true,
                                                show_claim_number: true,
                                                cover_photo_id: defaultCoverPhotoId
                                            }
                                        })).catch(err => console.error('Failed to save default settings:', err));
                                    }
                                    setLoading(false);
                                } else {
                                    // Get selected images from location.state or API response
                                    const selectedImageIds = reportData?.selected_images ||
                                        reportData?.selectedImages ||
                                        freshReport.selected_images ||
                                        freshReport.selectedImages ||
                                        [];

                                    if (selectedImageIds.length > 0) {
                                        const sectionData = {
                                            title: 'Section 1',
                                            summary: '',
                                            order: 0
                                        };

                                        dispatch(createSection(freshReport.id, sectionData))
                                            .then((newSection) => {
                                                const sectionId = newSection.id || newSection.data?.id;

                                                // Add photos one by one
                                                const photoPromises = selectedImageIds.map((imageId, index) =>
                                                    dispatch(addPhotoToSection(sectionId, {
                                                        inspection_image_id: imageId,
                                                        order_index: index
                                                    }))
                                                );

                                                return Promise.all(photoPromises);
                                            })
                                            .then(() => {
                                                // Reload sections after adding photos
                                                return dispatch(getReportSections(freshReport.id));
                                            })
                                            .then((sectionsData) => {
                                                const updatedSections = Array.isArray(sectionsData) ? sectionsData : (sectionsData.data || []);
                                                setSections(updatedSections);
                                                // Set default cover photo from first section's first image and save settings
                                                if (updatedSections.length > 0 && updatedSections[0].photos?.length > 0) {
                                                    const defaultCoverPhotoId = updatedSections[0].photos[0].id;
                                                    setCoverSettings(prev => ({
                                                        ...prev,
                                                        coverPhotoId: defaultCoverPhotoId
                                                    }));
                                                    // Save default settings
                                                    dispatch(updateReportSettings(freshReport.id, {
                                                        cover_settings: {
                                                            show_cover_photo: true,
                                                            show_company_logo: true,
                                                            show_company_name: true,
                                                            show_created_date: true,
                                                            show_photo_count: true,
                                                            show_claim_number: true,
                                                            cover_photo_id: defaultCoverPhotoId
                                                        }
                                                    })).catch(err => console.error('Failed to save default settings:', err));
                                                }
                                                setLoading(false);
                                            })
                                            .catch((error) => {
                                                console.error('Failed to create initial section:', error);
                                                message.error(error.response?.data?.message || 'Failed to add photos to section');
                                                setSections([]);
                                                setLoading(false);
                                            });
                                    } else {
                                        setSections([]);
                                        setLoading(false);
                                    }
                                }
                            })
                            .catch((error) => {
                                console.error('Failed to load sections:', error);
                                message.error('Failed to load sections');
                                setSections([]);
                                setLoading(false);
                            });
                    })
                    .catch((error) => {
                        message.error('Failed to load project data');
                        console.error(error);
                        navigate(`/project-detail/?project_id=${projectId}`);
                    });
            })
            .catch((error) => {
                message.error('Failed to load report data');
                console.error(error);
                navigate('/projects');
            });
    }, [location, dispatch, navigate, userDetails, loadSections]);

    // Actions
    const actions = {
        // Section actions
        addSection: useCallback(() => {
            if (!report?.id) return;

            setAddingSection(true);
            const sectionData = {
                title: `Section ${sections.length + 1}`,
                summary: '',
                order: sections.length
            };

            dispatch(createSection(report.id, sectionData))
                .then(() => {
                    return loadSections(report.id);
                })
                .then(() => {
                    setActiveSectionIndex(sections.length);
                    message.success('Section created successfully');
                })
                .catch((error) => {
                    console.error('Failed to create section:', error);
                    message.error('Failed to create section');
                })
                .finally(() => {
                    setAddingSection(false);
                });
        }, [report, sections, dispatch, loadSections]),

        deleteSection: useCallback((sectionIndex) => {
            if (!report?.id) return Promise.resolve();
            if (sectionIndex === 0) {
                message.warning('First section cannot be deleted');
                return Promise.resolve();
            }

            const section = sections[sectionIndex];
            return dispatch(deleteSectionAPI(report.id, section.id))
                .then(() => {
                    loadSections(report.id);
                    setActiveSectionIndex(0);
                    message.success('Section deleted successfully');
                })
                .catch((error) => {
                    console.error('Failed to delete section:', error);
                    message.error('Failed to delete section');
                    throw error;
                });
        }, [report, sections, dispatch, loadSections]),

        updateSectionTitle: useCallback(async (sectionIndex, newTitle) => {
            if (!report?.id || !newTitle || newTitle.trim() === '') {
                message.error('Section title cannot be empty');
                return;
            }

            setSavingSectionTitle(true);
            try {
                const section = sections[sectionIndex];
                await dispatch(updateSection(report.id, section.id, { title: newTitle }));
                message.success('Section title updated successfully');
                await loadSections(report.id);
            } catch (error) {
                console.error('Failed to update title:', error);
                message.error('Failed to update title');
            } finally {
                setSavingSectionTitle(false);
            }
        }, [report, sections, dispatch, loadSections]),

        updateSectionSummary: useCallback(async (sectionIndex, newSummary) => {
            if (!report?.id) return;

            setSavingSectionSummary(true);
            try {
                const section = sections[sectionIndex];
                await dispatch(updateSection(report.id, section.id, { title: section.title, summary: newSummary }));
                message.success('Section summary updated successfully');
                await loadSections(report.id);
            } catch (error) {
                console.error('Failed to update summary:', error);
                message.error('Failed to update summary');
            } finally {
                setSavingSectionSummary(false);
            }
        }, [report, sections, dispatch, loadSections]),

        // Photo actions
        addPhotosToSection: useCallback((sectionIndex, imageIds, insertIndex = null) => {
            if (!report?.id || imageIds.length === 0) return;

            const section = sections[sectionIndex];

            // Filter out images that are already in the section
            const existingImageIds = section.photos?.map(p => p.inspection_image_id) || [];
            const newImages = imageIds.filter(id => !existingImageIds.includes(id));

            if (newImages.length === 0) {
                message.warning('All selected photos are already in this section');
                return;
            }

            // Add only new photos
            const photoPromises = newImages.map((imageId, index) => {
                const currentPhotoCount = section.photos?.length || 0;
                const orderIndex = insertIndex !== null ? insertIndex + index : currentPhotoCount + index;

                return dispatch(addPhotoToSection(section.id, {
                    inspection_image_id: imageId,
                    order_index: orderIndex
                }));
            });

            const skippedCount = imageIds.length - newImages.length;

            return Promise.all(photoPromises)
                .then(() => {
                    loadSections(report.id);
                    if (skippedCount > 0) {
                        message.success(`${newImages.length} photo(s) added, ${skippedCount} already existed`);
                    } else {
                        message.success(`${newImages.length} photo(s) added to section`);
                    }
                })
                .catch((error) => {
                    console.error('Failed to add photos:', error);
                    message.error(error.response?.data?.message || 'Failed to add photos');
                    throw error;
                });
        }, [report, sections, dispatch, loadSections]),

        deletePhotoFromSection: useCallback(async (sectionIndex, photoId) => {
            if (!report?.id) return;

            if (coverSettings.showCoverPhoto && coverSettings.coverPhotoId === photoId) {
                message.warning('Cannot delete cover photo. Please select a different cover photo first.');
                return;
            }

            try {
                const freshSections = await dispatch(getReportSections(report.id));
                const apiSections = Array.isArray(freshSections) ? freshSections : (freshSections.data || []);
                const currentSection = apiSections[sectionIndex];

                if (!currentSection) {
                    message.error('Section not found');
                    loadSections(report.id);
                    return;
                }

                const photoExists = currentSection.photos?.some(p => p.id === photoId);
                if (!photoExists) {
                    message.warning('Photo already deleted');
                    loadSections(report.id);
                    return;
                }

                await dispatch(removePhotoFromSection(currentSection.id, photoId));
                loadSections(report.id);
                message.success('Photo removed from section');
            } catch (error) {
                console.error('Failed to delete photo:', error);
                if (error.response?.status === 404) {
                    message.warning('Photo already deleted');
                } else {
                    const errorMsg = error.response?.data?.message ||
                        error.response?.data?.error ||
                        'Failed to delete photo';
                    message.error(errorMsg);
                }
                loadSections(report.id);
            }
        }, [report, coverSettings, dispatch, loadSections]),

        updatePhotoDescription: useCallback(async (sectionIndex, photoId, newDesc) => {
            if (!report?.id) return;

            const projectId = report?.project_id || report?.projectId;
            const inspectionImageId = sections[sectionIndex].photos.find(p => p.id === photoId)?.inspection_image_id;

            if (!inspectionImageId) {
                message.error('Image ID not found');
                return;
            }

            setSavingPhotoDescId(photoId);
            try {
                const formData = { project_id: projectId, description: newDesc };
                await dispatch(updateProjectImage(projectId, inspectionImageId, formData));
                await loadSections(report.id);
                message.success('Description updated successfully!');
            } catch (error) {
                message.error('Failed to update description');
                console.error(error);
            } finally {
                setSavingPhotoDescId(null);
            }
        }, [report, sections, dispatch, loadSections]),

        movePhoto: useCallback((sectionIndex, dragIndex, hoverIndex) => {
            // Optimistic update
            const section = sections[sectionIndex];
            const newPhotos = [...section.photos];
            const dragPhoto = newPhotos[dragIndex];
            newPhotos.splice(dragIndex, 1);
            newPhotos.splice(hoverIndex, 0, dragPhoto);

            // Update local state immediately
            setSections(prev => {
                const updated = [...prev];
                updated[sectionIndex] = { ...updated[sectionIndex], photos: newPhotos };
                return updated;
            });

            // Save to API
            const photoIds = newPhotos.map(p => p.id);
            dispatch(reorderPhotos(section.id, photoIds))
                .then(() => {
                    loadSections(report.id);
                })
                .catch((error) => {
                    console.error('Failed to reorder photos:', error);
                    message.error('Failed to reorder photos');
                    // Revert on error
                    loadSections(report.id);
                });
        }, [report, sections, dispatch, loadSections]),

        // Settings actions
        saveReportSettings: useCallback(async () => {
            if (!report?.id) return false;

            setSavingSettings(true);
            try {
                const profile = userDetails?.profiles?.[0];
                const logoUrl = profile?.logo_url || '';

                const companyData = {
                    company_name: profile?.organization_name || '',
                    company_logo: logoUrl,
                    inspector_name: profile?.insp_fullname || ''
                };

                const settingsData = {
                    title: editTitle,
                    photos_per_page: editLayout,
                    claim_number: claimNumber,
                    show_conclusion: showConclusion,
                    conclusion: conclusion,
                    cover_settings: {
                        show_cover_photo: coverSettings.showCoverPhoto,
                        show_company_logo: coverSettings.showCompanyLogo,
                        show_company_name: coverSettings.showCompanyName,
                        show_created_date: coverSettings.showCreatedDate,
                        show_photo_count: coverSettings.showPhotoCount,
                        show_claim_number: coverSettings.showClaimNumber,
                        cover_photo_id: coverSettings.coverPhotoId
                    },
                    company_data: companyData
                };

                const response = await dispatch(updateReportSettings(report.id, settingsData));
                const updatedReport = {
                    ...report,
                    title: response.title || editTitle,
                    photos_per_page: response.photos_per_page || editLayout,
                    photosPerPage: response.photos_per_page || editLayout
                };
                setReport(updatedReport);
                setEditTitle(response.title || editTitle);
                setEditLayout(response.photos_per_page || editLayout);
                setShowConclusion(response.show_conclusion ?? showConclusion);
                setConclusion(response.conclusion || conclusion);
                message.success('Settings saved successfully!');
                return true;
            } catch (error) {
                console.error('Save settings error:', error);
                message.error('Failed to save settings');
                return false;
            } finally {
                setSavingSettings(false);
            }
        }, [report, editTitle, editLayout, claimNumber, showConclusion, conclusion, coverSettings, userDetails, dispatch]),

        updateCoverPhoto: useCallback((photoId) => {
            if (!report?.id) return;

            setCoverSettings(prev => ({
                ...prev,
                coverPhotoId: photoId
            }));

            // Save settings immediately when cover photo is changed
            dispatch(updateReportSettings(report.id, {
                cover_settings: {
                    show_cover_photo: coverSettings.showCoverPhoto,
                    show_company_logo: coverSettings.showCompanyLogo,
                    show_company_name: coverSettings.showCompanyName,
                    show_created_date: coverSettings.showCreatedDate,
                    show_photo_count: coverSettings.showPhotoCount,
                    show_claim_number: coverSettings.showClaimNumber,
                    cover_photo_id: photoId
                }
            })).then((response) => {
                // Update report state with new cover settings
                setReport(prev => ({
                    ...prev,
                    cover_settings: {
                        ...prev.cover_settings,
                        cover_photo_id: photoId
                    }
                }));
                message.success('Cover photo updated');
            }).catch(err => {
                console.error('Failed to save cover photo:', err);
                message.error('Failed to save cover photo');
            });
        }, [report, coverSettings, dispatch]),

        generatePDF: useCallback(async () => {
            if (!report?.id) {
                message.error('Report ID not found');
                return;
            }

            setGeneratingPDF(true);

            try {
                const profile = userDetails?.profiles?.[0];
                const logoUrl = profile?.logo_url || '';

                const companyData = {
                    company_name: profile?.organization_name || '',
                    company_logo: logoUrl,
                    inspector_name: profile?.insp_fullname || ''
                };

                // Save company data to report before generating PDF
                await dispatch(updateReportSettings(report.id, { company_data: companyData }));

                const response = await dispatch(generatePDF(report.id, companyData));
                const pdfId = response?.pdf_id || response?.data?.pdf_id;

                if (!pdfId) {
                    message.error('PDF ID not received');
                    setGeneratingPDF(false);
                    return;
                }

                message.info('PDF generation started...');

                // Poll for PDF status
                const checkPDFStatus = async () => {
                    try {
                        const pdfResponse = await dispatch(downloadPDF(report.id, pdfId));

                        const pdfUrl = pdfResponse?.download_url || pdfResponse?.data?.download_url ||
                            pdfResponse?.pdf_url || pdfResponse?.data?.pdf_url ||
                            pdfResponse?.url || pdfResponse?.data?.url;

                        if (pdfUrl) {
                            message.success('PDF generated successfully!');
                            const link = document.createElement('a');
                            link.href = pdfUrl;
                            link.download = pdfResponse?.file_name || pdfResponse?.data?.file_name || `${report.title || 'report'}.pdf`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            setGeneratingPDF(false);
                        } else if (pdfResponse?.status === 'failed' || pdfResponse?.data?.status === 'failed') {
                            message.error('PDF generation failed');
                            setGeneratingPDF(false);
                        } else {
                            // Still generating, check again after 2 seconds
                            setTimeout(checkPDFStatus, 2000);
                        }
                    } catch (error) {
                        console.error('PDF status check error:', error);
                        message.error('Failed to check PDF status');
                        setGeneratingPDF(false);
                    }
                };

                // Start polling after 2 seconds
                setTimeout(checkPDFStatus, 2000);

            } catch (error) {
                console.error('PDF generation error:', error);
                message.error(error.response?.data?.message || 'Failed to generate PDF');
                setGeneratingPDF(false);
            }
        }, [report, userDetails, dispatch]),

        updateConclusion: useCallback(async (newConclusion) => {
            if (!report?.id) return;

            setSavingConclusion(true);
            try {
                await dispatch(updateReportSettings(report.id, {
                    conclusion: newConclusion,
                    show_conclusion: showConclusion
                }));
                setConclusion(newConclusion);
                setReport(prev => ({
                    ...prev,
                    conclusion: newConclusion
                }));
                message.success('Conclusion saved successfully');
            } catch (err) {
                message.error('Failed to save conclusion');
            } finally {
                setSavingConclusion(false);
            }
        }, [report, showConclusion, dispatch]),

        deleteReport: useCallback(async () => {
            if (!report?.id) return;

            try {
                await dispatch(deleteReportAPI(report.id));
                message.success('Report deleted successfully');
                const projectId = report?.project_id || report?.projectId;
                if (projectId) {
                    navigate(`/project-detail/?project_id=${projectId}`);
                } else {
                    navigate('/projects');
                }
            } catch (error) {
                console.error('Failed to delete report:', error);
                message.error('Failed to delete report');
            }
        }, [report, dispatch, navigate])
    };

    return {
        // Data
        report,
        sections,
        setSections,
        projectData,
        projectImages,
        layoutOptions,

        // Settings
        editTitle,
        setEditTitle,
        editLayout,
        setEditLayout,
        claimNumber,
        setClaimNumber,
        showConclusion,
        setShowConclusion,
        conclusion,
        setConclusion,
        coverSettings,
        setCoverSettings,

        // UI state
        activeSectionIndex,
        setActiveSectionIndex,
        loading,
        generatingPDF,
        savingSettings,
        addingSection,
        savingConclusion,
        savingSectionTitle,
        savingSectionSummary,
        savingPhotoDescId,

        // Actions
        actions,
        loadSections: () => loadSections(report?.id)
    };
};

export default usePhotoReport;


